import * as Accordion from "@radix-ui/react-accordion";
import clsx from "clsx";
import Image from "next/image";
import { useContext, type ReactNode } from "react";

import { GlobalMenuContext } from "../../_contexts/global-menu";
import { Icons } from "../icons";
import { Link } from "../ui/link";

type MenuItemType = "desktopLink" | "mobileLink" | "mobileAccordionChild";

type MenuItemProps = {
  icon?: string;
  href?: string;
  children: ReactNode;
  isExternal?: boolean;
  type?: MenuItemType;
  textColor?: string;
};

export default function MenuItem({
  icon,
  href,
  children,
  isExternal,
  type = "desktopLink",
  textColor,
}: MenuItemProps) {
  return (
    <ConditionalLink href={href} isExternal={isExternal}>
      <div
        className={clsx("flex flex-row items-center gap-[6px]", {
          "h-[60px] px-[30px]": type === "mobileLink",
          "h-[40px]": type === "mobileAccordionChild",
          "min-h-[20px]": type === "desktopLink",
        })}
      >
        {icon && <Image src={icon} alt={icon} width={18} height={18} />}
        <div
          className={`flex min-w-[10px] grow text-[12px] leading-[1.3] font-medium wrap-break-word ${
            textColor ?? "text-skogecha"
          }`}
        >
          {children}
        </div>
      </div>
    </ConditionalLink>
  );
}

// Accordion.Triggerの中にLinkがあると正しくAccordionが動作しない（childrenの文字をクリックしても開閉しない）ため、hrefがない場合はLinkを出さないようにする
function ConditionalLink({
  children,
  href,
  isExternal,
}: {
  children: ReactNode;
  href?: string;
  isExternal?: boolean;
}) {
  const { setIsMenuOpen } = useContext(GlobalMenuContext);
  return href ? (
    // Linkではページが完全にリロードされてないため、ログアウトのエンドピントはちゃんと動かない。
    // Auth0のセッションは正しくログアウトされるため<a>タグを利用。
    href === "/auth/logout" ? (
      <a
        data-analytics-id="global_menu_clicked_logout"
        href={href}
        target={isExternal ? "_blank" : undefined}
        onClick={() => {
          if (!isExternal) setIsMenuOpen(false);
        }}
        className={clsx({ "cursor-default hover:opacity-100": !href })}
      >
        {children}
      </a>
    ) : (
      <Link
        data-analytics-id="global_menu_clicked_entry"
        href={href}
        target={isExternal ? "_blank" : undefined}
        onClick={() => {
          if (!isExternal) setIsMenuOpen(false);
        }}
        className={clsx({ "cursor-default hover:opacity-100": !href })}
      >
        {children}
      </Link>
    )
  ) : (
    children
  );
}

export function MenuAccordionItem({
  title,
  iconPath,
  children,
  textColor,
}: {
  title: string;
  iconPath: string;
  children: React.ReactNode;
  textColor?: string;
}) {
  return (
    <Accordion.Item value={title}>
      <Accordion.Header>
        <Accordion.Trigger
          className={clsx(
            "h-[60px] pr-[44px] pl-[30px]",
            "group flex w-full items-center justify-between",
          )}
        >
          <div className="text-[14px] group-data-[state=open]:font-bold">
            <MenuItem icon={iconPath} textColor={textColor}>
              {title}
            </MenuItem>
          </div>
          <Icons.ChevronDownIcon
            className={clsx(
              "size-[18px] transition-transform duration-300 group-data-[state=open]:rotate-180",
              textColor,
            )}
          />
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className="sa-content data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden px-[30px]">
        {children}
        <div className="h-[15px]"></div>
      </Accordion.Content>
    </Accordion.Item>
  );
}
